import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t, type Locale } from "../i18n";
import { settings, type Settings } from "../store/settings";
import { progress } from "../store/progress";
import { setBgm, setBgmVolume, unlockAudio } from "../audio/sfx";
import { claimDisplayName, exportBackup, importBackup, playerHandle, push, syncState } from "../net/sync";
import { openProfilePicker, profileLevel } from "./profile";
import { tier } from "../game/tiers";
import { tierSwatch } from "../render/renderer";

const APP_VERSION = "0.6.0 (M6)";

export class SettingsScreen implements Screen {
  private off?: () => void;
  private host!: HTMLElement;
  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    this.host = host;
    const screen = el("div", "screen settings");
    const top = buildTopbar({ title: "menu.settings", onBack: () => this.nav.home() });

    const body = el("div", "settings-body");
    screen.append(top.node, body);
    host.appendChild(screen);

    const render = (): void => {
      top.refresh();
      body.innerHTML = "";
      body.append(
        this.displaySection(),
        this.soundSection(),
        this.graphicsSection(),
        this.accessibilitySection(),
        this.gameplaySection(),
        this.dataSection(render),
        this.infoSection(),
      );
    };
    render();
    this.off = i18n.onChange(render);
  }

  unmount(): void {
    this.off?.();
  }

  // ---- control builders ----
  private section(titleKey: string): HTMLElement {
    const s = el("div", "settings-section");
    s.appendChild(el("h3", "settings-h", t(titleKey)));
    return s;
  }

  private toggle(labelKey: string, value: boolean, on: (v: boolean) => void): HTMLElement {
    const row = el("div", "set-row");
    row.appendChild(el("span", "set-label", t(labelKey)));
    const sw = el("button", `switch ${value ? "on" : ""}`);
    sw.setAttribute("role", "switch");
    sw.setAttribute("aria-checked", String(value));
    sw.innerHTML = `<span class="knob"></span>`;
    sw.onclick = () => {
      const next = !sw.classList.contains("on");
      sw.classList.toggle("on", next);
      sw.setAttribute("aria-checked", String(next));
      on(next);
    };
    row.appendChild(sw);
    return row;
  }

  private segmented<T extends string | number>(
    labelKey: string,
    options: Array<{ value: T; label: string }>,
    value: T,
    on: (v: T) => void,
  ): HTMLElement {
    const row = el("div", "set-row");
    row.appendChild(el("span", "set-label", t(labelKey)));
    const group = el("div", "segmented");
    for (const opt of options) {
      const b = el("button", `seg ${opt.value === value ? "active" : ""}`, opt.label);
      b.onclick = () => {
        group.querySelectorAll(".seg").forEach((e) => e.classList.remove("active"));
        b.classList.add("active");
        on(opt.value);
      };
      group.appendChild(b);
    }
    row.appendChild(group);
    return row;
  }

  private slider(
    labelKey: string,
    value: number,
    min: number,
    max: number,
    step: number,
    on: (v: number) => void,
  ): HTMLElement {
    const row = el("div", "set-row");
    row.appendChild(el("span", "set-label", t(labelKey)));
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.className = "slider";
    input.oninput = () => on(Number(input.value));
    row.appendChild(input);
    return row;
  }

  /**
   * Display-name row with server-side uniqueness check. On commit (blur/Enter)
   * the name is reserved via the backend; a duplicate or invalid name is
   * rejected with feedback and the field reverts to the saved value.
   */
  private displayNameRow(): HTMLElement {
    const row = el("div", "set-row name-row");
    row.appendChild(el("span", "set-label", t("set.displayName")));
    const field = el("div", "name-field");
    const input = document.createElement("input");
    input.type = "text";
    input.className = "name-input";
    input.value = settings.get("displayName");
    input.placeholder = playerHandle();
    input.maxLength = 24;
    const status = el("div", "name-status");
    field.append(input, status);
    row.appendChild(field);

    const setStatus = (key: string, kind: string): void => {
      status.textContent = key ? t(key) : "";
      status.className = `name-status ${kind}`;
    };

    input.onchange = () => {
      const v = input.value.trim().slice(0, 24);
      const current = settings.get("displayName");
      if (v === current) {
        setStatus("", "");
        return;
      }
      setStatus("set.name.checking", "checking");
      input.disabled = true;
      void claimDisplayName(v).then((r) => {
        input.disabled = false;
        input.focus();
        if (r === "ok") {
          this.set("displayName", v);
          setStatus(v ? "set.name.available" : "", "ok");
        } else if (r === "taken") {
          setStatus("set.name.taken", "err");
          input.value = current;
        } else if (r === "invalid") {
          setStatus("set.name.invalid", "err");
        } else {
          setStatus("set.name.offline", "err");
          input.value = current;
        }
      });
    };
    return row;
  }

  private set<K extends keyof Settings>(key: K, value: Settings[K]): void {
    settings.set(key, value);
  }

  private profileRow(render: () => void): HTMLElement {
    const row = el("div", "set-row");
    row.appendChild(el("span", "set-label", t("profile.pick")));
    const lv = profileLevel();
    const btn = el("button", "profile-btn");
    btn.innerHTML = `${tierSwatch(lv, 30)}<span>${t(`tier.${tier(lv).key}`)}</span>`;
    btn.onclick = () => openProfilePicker(this.host, render);
    row.appendChild(btn);
    return row;
  }

  // ---- sections ----
  private displaySection(): HTMLElement {
    const s = this.section("set.display");
    s.appendChild(
      this.segmented<Locale>(
        "menu.language",
        [
          { value: "ko", label: "한국어" },
          { value: "en", label: "English" },
        ],
        i18n.current,
        (v) => i18n.set(v),
      ),
    );
    s.appendChild(
      this.segmented(
        "set.notation",
        [
          { value: "name", label: t("set.notation.name") },
          { value: "roman", label: "VI" },
          { value: "number", label: "6" },
          { value: "none", label: t("set.notation.none") },
        ],
        settings.get("tierNotation"),
        (v) => this.set("tierNotation", v),
      ),
    );
    s.appendChild(
      this.toggle("set.numberAbbrev", settings.get("numberAbbrev"), (v) =>
        this.set("numberAbbrev", v),
      ),
    );
    return s;
  }

  private soundSection(): HTMLElement {
    const s = this.section("set.sound");
    s.appendChild(
      this.toggle("set.sfx", settings.get("sfx"), (v) => {
        unlockAudio();
        this.set("sfx", v);
      }),
    );
    s.appendChild(
      this.slider("set.sfxVolume", settings.get("sfxVolume"), 0, 1, 0.05, (v) =>
        this.set("sfxVolume", v),
      ),
    );
    s.appendChild(
      this.toggle("set.bgm", settings.get("bgm"), (v) => {
        unlockAudio();
        this.set("bgm", v);
        setBgm(v);
      }),
    );
    s.appendChild(
      this.slider("set.bgmVolume", settings.get("bgmVolume"), 0, 1, 0.05, (v) => {
        this.set("bgmVolume", v);
        setBgmVolume(v);
      }),
    );
    s.appendChild(this.toggle("set.haptics", settings.get("haptics"), (v) => this.set("haptics", v)));
    return s;
  }

  private graphicsSection(): HTMLElement {
    const s = this.section("set.graphics");
    s.appendChild(
      this.segmented(
        "set.particles",
        (["high", "normal", "low", "off"] as const).map((v) => ({ value: v, label: t(`set.level.${v}`) })),
        settings.get("particles"),
        (v) => this.set("particles", v),
      ),
    );
    s.appendChild(this.toggle("set.glow", settings.get("glow"), (v) => this.set("glow", v)));
    s.appendChild(
      this.segmented(
        "set.animSpeed",
        (["slow", "normal", "fast", "instant"] as const).map((v) => ({
          value: v,
          label: t(`set.speed.${v}`),
        })),
        settings.get("animSpeed"),
        (v) => this.set("animSpeed", v),
      ),
    );
    s.appendChild(this.toggle("set.shake", settings.get("screenShake"), (v) => this.set("screenShake", v)));
    s.appendChild(this.toggle("set.starfield", settings.get("starfield"), (v) => this.set("starfield", v)));
    s.appendChild(this.toggle("set.reduceMotion", settings.get("reduceMotion"), (v) => this.set("reduceMotion", v)));
    return s;
  }

  private accessibilitySection(): HTMLElement {
    const s = this.section("set.accessibility");
    s.appendChild(this.toggle("set.highContrast", settings.get("highContrast"), (v) => this.set("highContrast", v)));
    s.appendChild(
      this.segmented(
        "set.fontSize",
        [
          { value: 0.9, label: "A-" },
          { value: 1, label: "A" },
          { value: 1.15, label: "A+" },
          { value: 1.3, label: "A++" },
        ],
        settings.get("fontScale"),
        (v) => this.set("fontScale", v),
      ),
    );
    return s;
  }

  private gameplaySection(): HTMLElement {
    const s = this.section("set.gameplay");
    s.appendChild(
      this.segmented(
        "set.dropControl",
        [
          { value: "tap", label: t("set.control.tap") },
          { value: "drag", label: t("set.control.drag") },
        ],
        settings.get("dropControl"),
        (v) => this.set("dropControl", v),
      ),
    );
    s.appendChild(
      this.segmented<1 | 2>(
        "set.previewCount",
        [
          { value: 1, label: "1" },
          { value: 2, label: "2" },
        ],
        settings.get("previewCount"),
        (v) => this.set("previewCount", v),
      ),
    );
    return s;
  }

  private dataSection(render: () => void): HTMLElement {
    const s = this.section("set.data");
    s.appendChild(this.displayNameRow());
    s.appendChild(this.profileRow(render));
    const st = syncState();
    const statusKey = st.online ? "set.sync.online" : "set.sync.offline";
    const status = el("div", "sync-status");
    status.innerHTML = `<span class="dot ${st.online ? "on" : ""}"></span>${t(statusKey)}`;
    s.appendChild(status);

    const exportBtn = el("button", "", t("set.backup.export"));
    exportBtn.onclick = async () => {
      await push();
      const code = await exportBackup();
      this.showCode(code);
    };
    const importBtn = el("button", "", t("set.backup.import"));
    importBtn.onclick = () => this.showImport(render);
    const row = el("div", "set-btn-row");
    row.append(exportBtn, importBtn);
    s.appendChild(row);

    const reset = el("button", "danger", t("set.reset"));
    reset.onclick = () => this.confirmReset(render);
    s.appendChild(reset);
    return s;
  }

  private infoSection(): HTMLElement {
    const s = this.section("set.info");
    const info = el("div", "info-block");
    info.innerHTML = `
      <div class="info-row"><span>${t("set.version")}</span><span>${APP_VERSION}</span></div>
      <div class="info-row"><span>${t("set.credits")}</span><span>SVG + CSS · Cloudflare</span></div>`;
    s.appendChild(info);
    return s;
  }

  // ---- modals ----
  private modal(): { backdrop: HTMLElement; modal: HTMLElement } {
    const backdrop = el("div", "modal-backdrop");
    const modal = el("div", "panel modal");
    backdrop.appendChild(modal);
    this.host.appendChild(backdrop);
    return { backdrop, modal };
  }

  private showCode(code: string | null): void {
    const { backdrop, modal } = this.modal();
    modal.innerHTML = `<h2>${t("set.backup.export")}</h2>
      <div class="sub">${code ? t("set.backup.codeHint") : t("set.backup.offline")}</div>
      ${code ? `<div class="backup-code">${code}</div>` : ""}`;
    const close = el("button", "btn-primary", t("common.close"));
    close.onclick = () => backdrop.remove();
    if (code) {
      const copy = el("button", "", t("common.copy"));
      copy.onclick = () => void navigator.clipboard?.writeText(code);
      const row = el("div", "modal-btns");
      row.append(copy, close);
      modal.appendChild(row);
    } else {
      modal.appendChild(close);
    }
  }

  private showImport(render: () => void): void {
    const { backdrop, modal } = this.modal();
    modal.innerHTML = `<h2>${t("set.backup.import")}</h2><div class="sub">${t("set.backup.importHint")}</div>`;
    const input = document.createElement("input");
    input.className = "code-input";
    input.placeholder = "XXXX-XXXX-…";
    modal.appendChild(input);
    const msg = el("div", "sub");
    modal.appendChild(msg);
    const go = el("button", "btn-primary", t("set.backup.import"));
    go.onclick = async () => {
      const ok = await importBackup(input.value.trim());
      if (ok) {
        backdrop.remove();
        render();
      } else {
        msg.textContent = t("set.backup.fail");
      }
    };
    const cancel = el("button", "", t("common.cancel"));
    cancel.onclick = () => backdrop.remove();
    const row = el("div", "modal-btns");
    row.append(go, cancel);
    modal.appendChild(row);
  }

  private confirmReset(render: () => void): void {
    const { backdrop, modal } = this.modal();
    modal.innerHTML = `<h2>${t("set.reset")}</h2><div class="sub">${t("set.reset.confirm1")}</div>`;
    const step2 = el("button", "danger", t("set.reset.step2"));
    step2.onclick = () => {
      modal.innerHTML = `<h2>${t("set.reset")}</h2><div class="sub">${t("set.reset.confirm2")}</div>`;
      const yes = el("button", "danger", t("set.reset.final"));
      yes.onclick = () => {
        progress.reset();
        void push();
        backdrop.remove();
        render();
      };
      const no = el("button", "", t("common.cancel"));
      no.onclick = () => backdrop.remove();
      const row = el("div", "modal-btns");
      row.append(yes, no);
      modal.appendChild(row);
    };
    const cancel = el("button", "", t("common.cancel"));
    cancel.onclick = () => backdrop.remove();
    const row = el("div", "modal-btns");
    row.append(step2, cancel);
    modal.appendChild(row);
  }
}
